// GIF search through KLIPY, called straight from the browser as their terms require (no server proxy, no caching,
// results shown in their order). KLIPY's response format isn't fully documented, so parsing is defensive and
// accepts both its native shape (file.{hd,md,sm,xs}.{webp,gif,mp4}) and the Tenor-style shape (media_formats).
import type { Attachment } from "@bloom/shared";

export type GifAttachment = Extract<Attachment, { kind: "gif" }>;

export interface GifResult {
  id: string;
  title: string;
  /** Small rendition for the picker grid. */
  preview: Rendition;
  /** Every small rendition KLIPY gave (xs, sm), narrowest first: the grid takes the smallest that fills its cell (#736). */
  previews: Rendition[];
  /** What gets posted in chat. */
  full: GifAttachment;
}

export type Rendition = { url: string; width: number; height: number; format: "image" | "video" };

function pick(variant: Record<string, unknown> | undefined): Rendition | null {
  if (!variant) return null;
  for (const [fmt, kind] of [["webp", "image"], ["gif", "image"], ["mp4", "video"]] as const) {
    const v = variant[fmt] as { url?: string; width?: number; height?: number } | undefined;
    if (v?.url && v.width && v.height) return { url: v.url, width: v.width, height: v.height, format: kind };
  }
  return null;
}

function parseItem(item: Record<string, unknown>): GifResult | null {
  const id = String(item.slug ?? item.id ?? "");
  const title = String(item.title ?? item.content_description ?? "");
  // Native KLIPY: file: { hd, md, sm, xs } each { gif|webp|mp4: { url, width, height } }
  const file = (item.file ?? item.files) as Record<string, Record<string, unknown>> | undefined;
  if (file) {
    const full = pick(file.md) ?? pick(file.hd) ?? pick(file.sm);
    const preview = pick(file.sm) ?? pick(file.xs) ?? full;
    const previews = [pick(file.xs), pick(file.sm)].filter((r): r is Rendition => !!r).sort((x, y) => x.width - y.width);
    if (full && preview) return { id, title, preview, previews: previews.length ? previews : [preview], full: { kind: "gif", provider: "klipy", id, title, ...full } };
  }
  // Tenor-compatible: media_formats: { gif|tinygif|mp4|tinymp4|webp: { url, dims: [w, h] } }
  const mf = item.media_formats as Record<string, { url?: string; dims?: number[] }> | undefined;
  if (mf) {
    const r = (k: string, format: "image" | "video"): Rendition | null => {
      const v = mf[k];
      return v?.url && v.dims?.[0] && v.dims[1] ? { url: v.url, width: v.dims[0], height: v.dims[1], format } : null;
    };
    const full = r("webp", "image") ?? r("gif", "image") ?? r("mp4", "video");
    const preview = r("tinywebp", "image") ?? r("tinygif", "image") ?? r("tinymp4", "video") ?? full;
    if (full && preview) return { id, title, preview, previews: [preview], full: { kind: "gif", provider: "klipy", id, title, ...full } };
  }
  return null;
}

export interface GifPage {
  results: GifResult[];
  hasNext: boolean;
}

/** customerId: a stable, anonymous id per person (KLIPY uses it for relevance). */
export async function searchGifs(key: string, query: string, page: number, customerId: string): Promise<GifPage> {
  const base = `https://api.klipy.com/api/v1/${encodeURIComponent(key)}/gifs/${query ? "search" : "trending"}`;
  const params = new URLSearchParams({ page: String(page), per_page: "24", customer_id: customerId, rating: "pg-13" });
  if (query) params.set("q", query);
  // The page sends other sites no Referer (worker/src/headers.ts); KLIPY's key may be tied to our origin.
  const res = await fetch(`${base}?${params}`, { referrerPolicy: "strict-origin-when-cross-origin" });
  if (!res.ok) throw new Error(`GIF search failed (${res.status}).`);
  const json = (await res.json()) as { data?: { data?: unknown[]; has_next?: boolean } | unknown[]; results?: unknown[] };
  const items = (Array.isArray(json.data) ? json.data : (json.data?.data ?? json.results ?? [])) as Record<string, unknown>[];
  return {
    results: items.map(parseItem).filter((x): x is GifResult => !!x),
    hasNext: !Array.isArray(json.data) && !!json.data?.has_next,
  };
}
